import { create } from 'zustand'
import { FUTU_GetLeverageBracket } from '@/entities/futures/api/futuApiKey'
import { FUTU_exchangeInfo } from '@/entities/futures/api/futu'
import { getFutuSymbol } from '@/shared/hooks/futures/useCommon'
import { IsNull, IsValid } from '../utils/validation'
import { settingBot } from '@/features/setting-bot/store/setting'
import { getFloatDotSize } from '../utils/formatter'

interface FutuExchangeStatus {
  currnetPrice: string
  setCurrnetPrice: (price: string) => void
  exSymbol: string
  exchangeInfo: Map<string, BinanceAPI_FUTU_exchangeInfo_Symbol> | null // exchangeInfo
  leverageBrackets: Map<string, FutuAPI_GetLeverageBracket_Res> | null // 사용자의 레버리지 설정 리스트
  getNotionalCap: (symbol: string, leverage: string) => number
  getFutuExchangeInfo: (symbol: string) =>
    | {
        minMargin?: string
        tickSize?: string
        maxQty?: string
        minQty?: string
        stepSize?: string
        market_maxQty?: string
        market_minQty?: string
        market_stepSize?: string
      }
    | undefined
}

interface FutuExchangeAction {
  BinanceExchangeInfo: () => void
  BinanceLeverageBrackets: () => void
  setFutuExchangeInfo: (args: BinanceAPI_FUTU_exchangeInfo_Res | null) => void
  setExSymbol: (symbol: string) => void
  // 유지마진 비율 조회
  GetMarginRatio: (symbol: string, notional: string) => number
  LeverageMaxPrice: (symbol: string, intLeverage: number) => number // 현재 레버리지에서 최대 금액
  getTickSize: (symbol: string) => number
}

type FutuExchangeInfo = FutuExchangeStatus & FutuExchangeAction

export const storeFutuExchange = create<FutuExchangeInfo>((set, get) => ({
  currnetPrice: '0',
  setCurrnetPrice: (price: string) => {
    set({ currnetPrice: price })
  },
  exSymbol: getFutuSymbol(),
  setExSymbol: (symbol: string) => {
    // 심볼 변경 시 레버리지: maxLeverage ≤ 20이면 maxLeverage, maxLeverage ≥ 20이면 20으로 설정
    try {
      const leverageBracketsMap = get().leverageBrackets
      const brackets = leverageBracketsMap?.get(symbol.toUpperCase())?.brackets || []
      if (brackets.length > 0) {
        const maxLeverage = Number(brackets[0]?.initialLeverage) || 20
        const nextLeverage = String(Math.min(maxLeverage, 20))
        settingBot.getState().setRequired('leverage', nextLeverage)
      }
    } catch (e) {
      // 에러 시 레버리지는 변경하지 않음
    }
    settingBot.getState().setRequired('id_tactic', symbol.split('USDT')[0])

    set({ exSymbol: symbol })
  },
  exchangeInfo: null,
  leverageBrackets: null,

  getTickSize: (symbol: string) => {
    const tickSize = getFloatDotSize(get().getFutuExchangeInfo(symbol)?.tickSize || '0')
    return tickSize
  },
  // 바이낸스에서 다시 가져오는 구문
  BinanceExchangeInfo: async () => {
    // console.log('BinanceExchangeInfo : ', get().exchangeInfo)
    const { exchangeInfo } = get()
    if (
      exchangeInfo === undefined || // exchangeInfo가 undefined인 경우
      exchangeInfo === null || // exchangeInfo가 null인 경우
      !(exchangeInfo instanceof Map) || // exchangeInfo가 Map이 아닌 경우
      (exchangeInfo instanceof Map && exchangeInfo.size === 0) // exchangeInfo가 Map이고 비어 있는 경우
    ) {
      const exchangeInfo = await FUTU_exchangeInfo()
      if ('code' in exchangeInfo) {
        return
      }
      get().setFutuExchangeInfo(exchangeInfo)
    }
  },
  BinanceLeverageBrackets: async () => {
    const leverageBrackets = await FUTU_GetLeverageBracket()
    if ('code' in leverageBrackets) {
      return
    }
    if (leverageBrackets === null) return
    const leverageBracketsMap = new Map(
      leverageBrackets.filter((el) => el.symbol.endsWith('USDT')).map((s) => [s.symbol.toUpperCase(), s]),
    )
    set({
      leverageBrackets: leverageBracketsMap,
    })
  },
  setFutuExchangeInfo: (args: BinanceAPI_FUTU_exchangeInfo_Res | null) => {
    if (args === null) return
    const filteredSymbols = args.symbols?.filter(
      (s) =>
        s.quoteAsset === 'USDT' &&
        (s.contractType === 'PERPETUAL' ||
          s.contractType === 'TRADIFI_PERPETUAL' ||
          s.contractType === 'PERPETUAL_CARRY') &&
        s.status === 'TRADING',
    )
    const exchangeInfoMap = new Map(filteredSymbols?.map((s) => [s.symbol.toUpperCase(), s]))
    set({ exchangeInfo: exchangeInfoMap })
  },
  getFutuExchangeInfo: (symbol: string) => {
    const { exchangeInfo } = get()
    if (!exchangeInfo || !(exchangeInfo instanceof Map)) return undefined
    const symbolInfo = exchangeInfo.get(symbol)
    if (symbolInfo) {
      const minMargin = symbolInfo.filters.find((f) => f.filterType === 'MIN_NOTIONAL')?.notional
      const tickSize = symbolInfo.filters.find((f) => f.filterType === 'PRICE_FILTER')?.tickSize
      const lotSize = symbolInfo.filters.find((f) => f.filterType === 'LOT_SIZE')
      const maxQty = lotSize?.maxQty
      const minQty = lotSize?.minQty
      const stepSize = lotSize?.stepSize
      const marketLotSize = symbolInfo.filters.find((f) => f.filterType === 'MARKET_LOT_SIZE')
      const market_maxQty = marketLotSize?.maxQty
      const market_minQty = marketLotSize?.minQty
      const market_stepSize = marketLotSize?.stepSize

      return { minMargin, tickSize, maxQty, minQty, stepSize, market_maxQty, market_minQty, market_stepSize }
    }
  },
  getNotionalCap: (symbol: string, leverage: string): number => {
    const leverageBrackets = get()?.leverageBrackets

    // leverageBrackets 유효성 검사 통합
    if (!leverageBrackets || leverageBrackets.size === 0) return 0

    const leverageBracket = leverageBrackets instanceof Map ? leverageBrackets.get(symbol.toUpperCase()) : null
    // console.log('leverageBracket', leverageBracket)
    if (!leverageBracket) return 0

    const obj = leverageBracket.brackets
    const targetLeverage = Number(leverage)

    // 정확히 일치하는 레버리지 찾기
    const exactMatch = obj.find((bracket) => bracket.initialLeverage === targetLeverage)
    if (exactMatch) {
      return exactMatch.notionalCap
    }

    // 내림차순으로 정렬된 브라켓에서 적절한 notionalCap 찾기
    // 브라켓은 보통 높은 레버리지부터 낮은 레버리지 순으로 정렬되어 있음
    for (let i = 0; i < obj.length; i++) {
      const currentBracket = obj[i]

      // 현재 브라켓의 레버리지가 목표 레버리지보다 작거나 같으면
      // 이전 브라켓의 notionalCap을 반환 (또는 첫 번째 브라켓이면 현재 브라켓의 notionalCap)
      if (currentBracket.initialLeverage <= targetLeverage) {
        return i > 0 ? obj[i - 1].notionalCap : currentBracket.notionalCap
      }
    }

    // 모든 브라켓의 레버리지가 목표보다 크면 마지막 브라켓의 notionalCap 반환
    return obj[obj.length - 1]?.notionalCap || 0
  },
  // 유지마진 비율 조회
  GetMarginRatio: (symbol: string, notional: string): number => {
    if (IsNull(symbol) || IsNull(notional)) return 0.5

    const leverageBrackets = get()?.leverageBrackets

    if (IsNull(leverageBrackets) || leverageBrackets?.size === 0) return 0.5

    if (leverageBrackets === null || leverageBrackets === undefined) return 0.5

    if (!IsValid(leverageBrackets)) {
      return 0.5
    }
    const leverageBracket = leverageBrackets?.get(symbol.toUpperCase())

    if (leverageBracket === null || leverageBracket === undefined) {
      return 0.5
    }

    const obj = leverageBracket.brackets
    for (let i = 0; i < obj.length; i++) {
      if (obj[i].notionalCap > Number(notional)) {
        return obj[i].maintMarginRatio
      }
    }
    return 0.5
  },
  // 현재 레버리지에서 최대 금액
  LeverageMaxPrice: (symbol: string, intLeverage: number) => {
    const upperSymbol = symbol.toUpperCase()
    const { leverageBrackets } = get()

    if (leverageBrackets === null || leverageBrackets === undefined) {
      return 0
    }

    if (Object.keys(leverageBrackets).length === 0) {
      return 0
    }

    const result = leverageBrackets?.get(upperSymbol)

    if (!result || !result.brackets || result.brackets.length === 0) {
      return 0
    }

    if (intLeverage === 1) {
      return result.brackets[result.brackets.length - 1].notionalCap
    }

    for (let i = 0; i < result.brackets.length - 1; i++) {
      if (result.brackets[i].initialLeverage >= intLeverage && result.brackets[i + 1].initialLeverage < intLeverage) {
        return result.brackets[i].notionalCap
      }
    }
    return 0
  },
}))
